/**
 * apiErrorText — coerce any thrown API error into a human-readable string
 * that is SAFE to render as a React child (Toast text, <Text>, etc.).
 *
 * Why this exists: FastAPI 422 (validation) responses return `detail` as an
 * ARRAY of objects — `[{ type, loc, msg, input }]`. Some non-422 paths return
 * `detail` as a single object. Passing that object/array straight into a Toast
 * `text2` (a React child) throws "Objects are not valid as a React child"
 * (minified React error #31), which the root ErrorBoundary catches and turns
 * into a full-screen "Oops! Something went wrong" crash.
 *
 * This was the exact crash on the Quick-add sheet: a quick-add 422 rendered the
 * Pydantic detail array → #31 → app crash. This helper flattens any shape —
 * string, Pydantic array, single `{msg}` object, or a plain Error — to a string.
 */
export function apiErrorText(err: any, fallback = 'Please try again.'): string {
  // Axios error → err.response.data.detail; some callers throw {status, detail}.
  const detail = err?.response?.data?.detail ?? err?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((d: any) => (typeof d === 'string' ? d : typeof d?.msg === 'string' ? d.msg : null))
      .filter(Boolean) as string[];
    if (parts.length) return parts.join('; ');
  }

  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') {
    return detail.msg;
  }

  if (typeof err?.message === 'string' && err.message.trim()) return err.message;

  return fallback;
}
